import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import CompanyLogo from "@/components/CompanyLogo";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend } from "recharts";
import { ArrowUpRight, TrendUp, TrendDown, Warning } from "@phosphor-icons/react";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let mounted = true;
        api.get("/dashboard/consolidated")
            .then((r) => { if (mounted) setData(r.data); })
            .catch((e) => setErr(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
        return () => { mounted = false; };
    }, []);

    const trend = useMemo(() => (data?.trend || []), [data]);
    const perCompany = data?.per_company || [];
    const gk = data?.group_kpis || {};

    return (
        <div className="p-8 lg:p-10 max-w-[1400px]" data-testid="dashboard-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline mb-2">Group Overview · Consolidated · USD</div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">
                        EXECUTIVE DASHBOARD
                    </h1>
                    <p className="text-sm text-muted2 mt-2 max-w-xl">
                        Real-time roll-up across all five Joseph Roberts Group units, built from every
                        WhatsApp screenshot and spreadsheet your teams have uploaded.
                        {data?.fx ? <span className="text-faint ml-1">· FX: £{data.fx.gbp_per_usd}/$ · Z${data.fx.zwl_per_usd}/$</span> : null}
                    </p>
                </div>
                <Link to="/upload" data-testid="cta-upload" className="btn-primary gap-2">
                    Upload report <ArrowUpRight size={16} />
                </Link>
            </div>

            {loading ? (
                <div className="overline" data-testid="dashboard-loading">Loading group data…</div>
            ) : err ? (
                <div className="text-neg" data-testid="dashboard-error">{String(err)}</div>
            ) : (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard testid="kpi-revenue" label="Total Revenue · USD" value={fmtUSD(gk.revenue_usd ?? gk.revenue)} sub="FX-normalised across units" tone="pos" />
                        <KpiCard testid="kpi-expenses" label="Total Expenses · USD" value={fmtUSD(gk.expenses_usd ?? gk.expenses)} sub="Operational outflows" tone="neg" />
                        <KpiCard testid="kpi-net" label="Net Position · USD" value={fmtUSD(gk.net_usd ?? gk.net)} sub="Revenue − expenses" tone={(gk.net_usd ?? gk.net) >= 0 ? "pos" : "neg"} />
                        <KpiCard testid="kpi-reports" label="Reports Processed" value={fmtNum(gk.report_count)} sub={`${fmtNum(gk.issues_count)} issues flagged`} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="grid-card p-6 lg:col-span-2">
                            <div className="overline mb-3">Revenue vs Expenses · last 30 reporting dates</div>
                            {trend.length === 0 ? (
                                <div className="h-64 flex items-center justify-center text-sm text-muted2">
                                    Upload reports to populate the trend chart.
                                </div>
                            ) : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
                                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#52525B" }} stroke="#E4E4E7" />
                                            <YAxis tick={{ fontSize: 11, fill: "#52525B" }} stroke="#E4E4E7" />
                                            <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #0A0A0A", fontSize: 12 }} />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                            <Line type="monotone" dataKey="revenue" stroke="#16A34A" strokeWidth={2} dot={false} />
                                            <Line type="monotone" dataKey="expenses" stroke="#DC2626" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                        <div className="grid-card p-6">
                            <div className="overline mb-3">Reports by Day</div>
                            {trend.length === 0 ? (
                                <div className="h-64 flex items-center justify-center text-sm text-muted2">No data yet.</div>
                            ) : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={trend}>
                                            <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
                                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#52525B" }} />
                                            <YAxis tick={{ fontSize: 10, fill: "#52525B" }} />
                                            <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #0A0A0A", fontSize: 12 }} />
                                            <Bar dataKey="reports" fill="#002FA7" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-end justify-between mb-3">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">Per-company performance</h2>
                            <Link to="/companies" className="text-xs underline text-muted2" data-testid="link-all-companies">View all companies →</Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {perCompany.map(({ company, kpis, latest_report }) => (
                                <Link
                                    key={company.id}
                                    to={`/companies/${company.slug}`}
                                    data-testid={`company-card-${company.slug}`}
                                    className="grid-card grid-card-hover p-6 group block"
                                >
                                    <div className="flex items-start gap-4 mb-4">
                                        <CompanyLogo company={company} size="md" />
                                        <div className="min-w-0">
                                            <div className="overline mb-1">{company.sector}</div>
                                            <div className="font-heading font-bold text-lg tracking-tight leading-tight">{company.name}</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 border-t border-rule pt-3">
                                        <div>
                                            <div className="overline">Revenue</div>
                                            <div className="data-num text-sm text-pos">{fmtUSD(kpis.revenue_usd ?? kpis.revenue)}</div>
                                        </div>
                                        <div>
                                            <div className="overline">Expenses</div>
                                            <div className="data-num text-sm text-neg">{fmtUSD(kpis.expenses_usd ?? kpis.expenses)}</div>
                                        </div>
                                        <div>
                                            <div className="overline">Reports</div>
                                            <div className="data-num text-sm">{fmtNum(kpis.report_count)}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-muted2 truncate">
                                        {latest_report ? (
                                            <>Latest: {latest_report.report_date} · {latest_report.summary?.slice(0, 80) || "—"}</>
                                        ) : (
                                            <span className="italic">No reports yet — upload to begin.</span>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="grid-card p-6">
                            <div className="flex items-center justify-between mb-3">
                                <div className="overline">Recent reports</div>
                                <Link to="/reports" className="text-xs underline text-muted2" data-testid="link-all-reports">All reports →</Link>
                            </div>
                            <div className="divide-y divide-rule">
                                {(data?.recent_reports || []).length === 0 ? (
                                    <div className="text-sm text-muted2 py-6">No reports yet.</div>
                                ) : (
                                    (data?.recent_reports || []).map((r) => (
                                        <Link to={`/reports/${r.id}`} key={r.id} data-testid={`recent-report-${r.id}`}
                                            className="block py-3 hover:bg-paper transition-colors">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-ink truncate">{r.company_name}</div>
                                                    <div className="text-xs text-muted2 truncate">{r.summary || r.extracted?.headline || "—"}</div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className="overline">{r.report_type === "financial" ? "Finance" : "Ops"}</div>
                                                    <div className="text-xs text-muted2 data-num">{r.report_date}</div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>
                        <div className="grid-card p-6">
                            <div className="overline mb-3">Health signals</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-start gap-3">
                                    <TrendUp size={18} className="text-pos shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-medium">Reporting cadence</div>
                                        <div className="text-muted2 text-xs">{fmtNum(gk.report_count)} reports processed across {perCompany.length} units.</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Warning size={18} className="text-warn shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-medium">Open issues</div>
                                        <div className="text-muted2 text-xs">{fmtNum(gk.issues_count)} issues raised by field teams in recent uploads.</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <TrendDown size={18} className={`${gk.net >= 0 ? "text-pos" : "text-neg"} shrink-0 mt-0.5`} />
                                    <div>
                                        <div className="font-medium">Net position</div>
                                        <div className="text-muted2 text-xs">{gk.net >= 0 ? "Group is net-positive in extracted figures." : "Group is currently net-negative; review high-cost units."}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
